<template>
  <section v-if="isBookingModuleEnabled && (loading || (merchants && merchants.length > 0))" class="booking-redirect-section">
    <!-- Section Header (Clean Minimalist Style, Matches Limited Offers & Transactions) -->
    <div class="booking-section-header">
      <h2 class="section-title">Our Properties</h2>
      <span
        v-if="merchants && merchants.length > 1"
        class="section-view-all"
        @click="openViewAllModal"
      >
        View all
      </span>
    </div>

    <!-- Skeleton Loading State -->
    <div v-if="isBookingModuleEnabled && loading" class="cards-track skeleton-track">
      <div v-for="n in 4" :key="'skel-' + n" class="hotel-card skeleton-card">
        <div class="skeleton-media shimmer"></div>
        <div class="skeleton-body">
          <div class="skeleton-line shimmer title-line"></div>
          <div class="skeleton-line shimmer loc-line"></div>
          <div class="skeleton-line shimmer btn-line"></div>
        </div>
      </div>
    </div>

    <!-- Merchant Cards Content -->
    <div
      v-else
      ref="scrollContainer"
      class="cards-track"
      :class="{
        'is-single': merchants.length === 1,
        'is-multi': merchants.length > 1
      }"
    >
      <article
        v-for="(merchant, index) in merchants"
        :key="merchant.merchant_id || index"
        class="hotel-card"
        @click="openPropertyDetail(merchant)"
      >
        <!-- Media / Property Logo Area -->
        <div class="hotel-card-media" :class="{ 'is-cover-photo': isUsingPhotoCover(merchant) }">
          <div class="logo-display-container">
            <img
              v-if="getMerchantImage(merchant)"
              :src="getMerchantImage(merchant)"
              :alt="merchant.name"
              class="merchant-logo-img"
              loading="lazy"
              @error="handleImageError(merchant)"
            />
            <div v-else class="fallback-hotel-logo">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1"/>
              </svg>
              <span class="fallback-code">{{ merchant.code || 'HOTEL' }}</span>
            </div>
          </div>
        </div>

        <!-- Card Body Details -->
        <div class="hotel-card-body">
          <div class="property-info-header">
            <h3 class="property-name" :title="merchant.name">{{ merchant.name }}</h3>
            <span v-if="merchant.branch_name && merchant.branch_name !== merchant.name" class="branch-name" :title="merchant.branch_name">
              {{ merchant.branch_name }}
            </span>
          </div>

          <!-- Location Row -->
          <div class="property-meta-row location-row">
            <svg class="meta-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <span class="meta-text" :title="merchant.full_address || merchant.city || merchant.address">
              {{ merchant.full_address || merchant.city || merchant.address || 'Registered property' }}
            </span>
          </div>

          <!-- Quick Contact Phone Row -->
          <div v-if="merchant.phone" class="property-meta-row contact-row">
            <a
              :href="'tel:' + merchant.phone"
              class="phone-chip"
              title="Call Hotel"
              @click.stop
            >
              <svg class="phone-chip-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              <span>{{ merchant.phone }}</span>
            </a>
          </div>

          <!-- Card Action Button -->
          <div class="hotel-card-action">
            <button
              type="button"
              class="btn-book-now"
              :disabled="redirectingId === merchant.merchant_id"
              @click.stop="handleBookNow(merchant)"
            >
              <span v-if="redirectingId === merchant.merchant_id" class="btn-content-loading">
                <svg class="spinner-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-dasharray="32" stroke-dashoffset="12"></circle>
                </svg>
                <span>Connecting...</span>
              </span>
              <span v-else class="btn-content-ready">
                <span>Book Now</span>
                <svg class="arrow-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </article>
    </div>

    <!-- 1. View All Properties Modal (Base Layer) -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div
          v-if="isViewAllModalVisible"
          class="custom-modal-backdrop view-all-backdrop"
          @click.self="closeViewAllModal"
        >
          <div class="custom-modal-card view-all-card" role="dialog" aria-modal="true">
            <div class="custom-modal-header">
              <h3 class="custom-modal-title">Our Properties</h3>
              <button
                type="button"
                class="custom-modal-close-btn"
                aria-label="Close"
                @click="closeViewAllModal"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <!-- Search input filter -->
            <div class="view-all-search-box">
              <svg class="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                class="search-input"
                placeholder="Search hotel name or city..."
              />
            </div>

            <!-- Scrollable list of properties -->
            <div class="view-all-list-body">
              <div v-if="filteredMerchants.length > 0" class="view-all-grid">
                <article
                  v-for="(item, idx) in filteredMerchants"
                  :key="'all-' + (item.merchant_id || idx)"
                  class="hotel-card"
                  @click="openPropertyDetail(item)"
                >
                  <div class="hotel-card-media" :class="{ 'is-cover-photo': isUsingPhotoCover(item) }">
                    <div class="logo-display-container">
                      <img
                        v-if="getMerchantImage(item)"
                        :src="getMerchantImage(item)"
                        :alt="item.name"
                        class="merchant-logo-img"
                        loading="lazy"
                        @error="handleImageError(item)"
                      />
                      <div v-else class="fallback-hotel-logo">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1"/>
                        </svg>
                        <span class="fallback-code">{{ item.code || 'HOTEL' }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="hotel-card-body">
                    <div class="property-info-header">
                      <h3 class="property-name" :title="item.name">{{ item.name }}</h3>
                      <span v-if="item.branch_name && item.branch_name !== item.name" class="branch-name">
                        {{ item.branch_name }}
                      </span>
                    </div>

                    <div class="property-meta-row location-row">
                      <svg class="meta-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <span class="meta-text" :title="item.full_address || item.city || item.address">
                        {{ item.full_address || item.city || item.address || 'Registered property' }}
                      </span>
                    </div>

                    <div class="hotel-card-action">
                      <button
                        type="button"
                        class="btn-book-now"
                        :disabled="redirectingId === item.merchant_id"
                        @click.stop="handleBookNow(item)"
                      >
                        <span v-if="redirectingId === item.merchant_id" class="btn-content-loading">
                          <svg class="spinner-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-dasharray="32" stroke-dashoffset="12"></circle>
                          </svg>
                          <span>Connecting...</span>
                        </span>
                        <span v-else class="btn-content-ready">
                          <span>Book Now</span>
                          <svg class="arrow-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </span>
                      </button>
                    </div>
                  </div>
                </article>
              </div>

              <div v-else class="view-all-empty">
                <p>No matching hotel properties found.</p>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 2. Property Detail Modal (Higher Layer) -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div
          v-if="selectedMerchantForDetail"
          class="custom-modal-backdrop property-detail-backdrop"
          @click.self="closePropertyDetail"
        >
          <div class="custom-modal-card property-detail-card" role="dialog" aria-modal="true">
            <div class="custom-modal-header">
              <h3 class="custom-modal-title">Property Details</h3>
              <button
                type="button"
                class="custom-modal-close-btn"
                aria-label="Close"
                @click="closePropertyDetail"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div class="property-detail-body">
              <!-- Property Logo Header -->
              <div class="detail-logo-banner" :class="{ 'is-cover-photo': isUsingPhotoCover(selectedMerchantForDetail) }">
                <img
                  v-if="getMerchantImage(selectedMerchantForDetail)"
                  :src="getMerchantImage(selectedMerchantForDetail)"
                  :alt="selectedMerchantForDetail.name"
                  class="detail-logo-img"
                  @error="handleImageError(selectedMerchantForDetail)"
                />
                <div v-else class="fallback-hotel-logo">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1"/>
                  </svg>
                  <span class="fallback-code">{{ selectedMerchantForDetail.code || 'HOTEL' }}</span>
                </div>
              </div>

              <!-- Property Details Info -->
              <div class="detail-info-group">
                <h4 class="detail-name">{{ selectedMerchantForDetail.name }}</h4>
                <p v-if="selectedMerchantForDetail.branch_name" class="detail-branch">
                  {{ selectedMerchantForDetail.branch_name }}
                </p>

                <!-- Address Item -->
                <div class="detail-meta-item">
                  <div class="detail-icon-circle">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </div>
                  <div class="detail-meta-text">
                    <span class="detail-label">Address</span>
                    <span class="detail-value">
                      {{ selectedMerchantForDetail.full_address || selectedMerchantForDetail.address || selectedMerchantForDetail.city || 'Address registered with hotel group' }}
                    </span>
                  </div>
                </div>

                <!-- Phone Item -->
                <div v-if="selectedMerchantForDetail.phone" class="detail-meta-item">
                  <div class="detail-icon-circle">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                  <div class="detail-meta-text">
                    <span class="detail-label">Direct Contact</span>
                    <a :href="'tel:' + selectedMerchantForDetail.phone" class="detail-phone-link">
                      {{ selectedMerchantForDetail.phone }}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Modal Footer Action -->
            <div class="custom-modal-footer">
              <button
                type="button"
                class="btn-detail-book"
                :disabled="redirectingId === selectedMerchantForDetail.merchant_id"
                @click="handleBookNow(selectedMerchantForDetail)"
              >
                <span v-if="redirectingId === selectedMerchantForDetail.merchant_id" class="btn-content-loading">
                  <svg class="spinner-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-dasharray="32" stroke-dashoffset="12"></circle>
                  </svg>
                  <span>Connecting to Booking Engine...</span>
                </span>
                <span v-else class="btn-content-ready">
                  <span>Book Now</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 3. Pop-up Blocker Fallback Modal Dialog (Top Layer) -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="isPopupBlockedModalVisible" class="custom-modal-backdrop blocker-backdrop" @click.self="closePopupBlockedModal">
          <div class="custom-modal-card blocker-card" role="dialog" aria-modal="true">
            <div class="blocker-icon-circle">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h3 class="custom-modal-title">Browser Pop-up Blocked</h3>
            <p class="blocker-desc">
              Your browser prevented opening the booking tab. Please click below to continue directly to the official hotel reservation page.
            </p>

            <div class="custom-modal-footer flex-col">
              <a
                v-if="blockedMerchant && blockedMerchant.booking_url"
                :href="blockedMerchant.booking_url"
                target="_blank"
                rel="noopener noreferrer"
                class="btn-detail-book"
                @click="closePopupBlockedModal"
              >
                Open Booking Page
              </a>
              <button type="button" class="btn-cancel-action" @click="closePopupBlockedModal">
                Close
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'MemberBookingMerchantCard',
  props: {
    merchants: {
      type: Array,
      default: () => []
    },
    authContext: {
      type: Object,
      default: () => null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore()
    const scrollContainer = ref(null)
    const redirectingId = ref(null)
    const isPopupBlockedModalVisible = ref(false)
    const blockedMerchant = ref(null)
    const selectedMerchantForDetail = ref(null)
    const isViewAllModalVisible = ref(false)
    const searchQuery = ref('')
    let handshakeInterval = null

    onBeforeUnmount(() => {
      if (handshakeInterval) {
        clearInterval(handshakeInterval)
      }
      document.body.style.overflow = ''
    })

    const lockBodyScroll = () => {
      document.body.style.overflow = 'hidden'
    }

    const unlockBodyScroll = () => {
      if (!selectedMerchantForDetail.value && !isViewAllModalVisible.value && !isPopupBlockedModalVisible.value) {
        document.body.style.overflow = ''
      }
    }

    const failedLogos = ref(new Set())
    const failedThemeBgs = ref(new Set())
    const failedDefaultBgs = ref(new Set())

    const themeLoginBackground = computed(() => {
      return (
        store?.state?.ThemeCorporate?.themeCorporate?.login_background ||
        localStorage.getItem('loginBackground') ||
        localStorage.getItem('login_background') ||
        '/img/temporary-login.png'
      )
    })

    const isBookingModuleEnabled = computed(() => {
      const flagObj = store?.state?.FeatureFlag?.bookingModule
      if (flagObj) {
        return flagObj.flag === 'ACTIVE'
      }
      if (store?.state?.FeatureFlag?.loading) {
        return false
      }
      return true
    })

    const getMerchantKey = (merchant) => {
      if (!merchant) return ''
      return String(merchant.merchant_id || merchant.property_id || merchant.name || 'default')
    }

    const getMerchantImage = (merchant) => {
      if (!merchant) return null
      const key = getMerchantKey(merchant)

      // Priority 1: Merchant's own logo
      if (merchant.logo_url && !failedLogos.value.has(key)) {
        return merchant.logo_url
      }

      // Priority 2: Corporate login background from theme
      const themeBg = themeLoginBackground.value
      if (themeBg && !failedThemeBgs.value.has(key)) {
        return themeBg
      }

      // Priority 3: Default codebase login background
      if (!failedDefaultBgs.value.has(key)) {
        return '/img/temporary-login.png'
      }

      // Final fallback: No image (SVG icon will be shown)
      return null
    }

    const isUsingPhotoCover = (merchant) => {
      if (!merchant) return true
      const key = getMerchantKey(merchant)
      return !merchant.logo_url || failedLogos.value.has(key)
    }

    const handleImageError = (merchant) => {
      if (!merchant) return
      const key = getMerchantKey(merchant)

      if (merchant.logo_url && !failedLogos.value.has(key)) {
        failedLogos.value = new Set(failedLogos.value).add(key)
        return
      }

      const themeBg = themeLoginBackground.value
      if (themeBg && !failedThemeBgs.value.has(key)) {
        failedThemeBgs.value = new Set(failedThemeBgs.value).add(key)
        return
      }

      failedDefaultBgs.value = new Set(failedDefaultBgs.value).add(key)
    }

    const openPropertyDetail = (merchant) => {
      selectedMerchantForDetail.value = merchant
      lockBodyScroll()
    }

    const closePropertyDetail = () => {
      selectedMerchantForDetail.value = null
      unlockBodyScroll()
    }

    const openViewAllModal = () => {
      isViewAllModalVisible.value = true
      searchQuery.value = ''
      lockBodyScroll()
    }

    const closeViewAllModal = () => {
      isViewAllModalVisible.value = false
      unlockBodyScroll()
    }

    const filteredMerchants = computed(() => {
      if (!props.merchants) return []
      const q = searchQuery.value.trim().toLowerCase()
      if (!q) return props.merchants
      return props.merchants.filter((m) => {
        const name = (m.name || '').toLowerCase()
        const branch = (m.branch_name || '').toLowerCase()
        const city = (m.city || '').toLowerCase()
        const state = (m.state || '').toLowerCase()
        return name.includes(q) || branch.includes(q) || city.includes(q) || state.includes(q)
      })
    })

    const handleBookNow = (merchant) => {
      if (!merchant || !merchant.booking_url) return

      redirectingId.value = merchant.merchant_id

      // 1. Open new window synchronously during user gesture
      const bookingWindow = window.open(merchant.booking_url, '_blank')

      if (!bookingWindow || bookingWindow.closed || typeof bookingWindow.closed === 'undefined') {
        blockedMerchant.value = merchant
        isPopupBlockedModalVisible.value = true
        lockBodyScroll()
        redirectingId.value = null
        return
      }

      const memberToken = props.authContext?.token || localStorage.getItem('token') || ''
      if (!memberToken) {
        setTimeout(() => {
          redirectingId.value = null
        }, 1500)
        return
      }

      // 2. Prepare handshake payload matching Booking Engine Navbar contract
      const payload = {
        token: memberToken,
        username: String(props.authContext?.username || '').trim(),
        title: String(props.authContext?.title || 'Mr'),
        first_name: String(props.authContext?.first_name || '').trim(),
        last_name: String(props.authContext?.last_name || '').trim(),
        email: String(props.authContext?.email || '').trim(),
        phone: String(props.authContext?.phone || '').trim(),
        tierId: props.authContext?.tier_id ? String(props.authContext.tier_id) : '',
        tier_name: String(props.authContext?.tier_name || 'Member'),
        point: Number(props.authContext?.points || 0),
        corporate_id: String(props.authContext?.corporate_id || '')
      }

      const targetOrigin = merchant.booking_origin || '*'

      if (handshakeInterval) {
        clearInterval(handshakeInterval)
      }

      // 3. PostMessage retry handshake loop (every 500ms for 3.5 seconds)
      let attempts = 0
      const maxAttempts = 7
      handshakeInterval = setInterval(() => {
        attempts++
        if (bookingWindow.closed || attempts >= maxAttempts) {
          clearInterval(handshakeInterval)
          handshakeInterval = null
          redirectingId.value = null
          return
        }

        try {
          bookingWindow.postMessage(payload, targetOrigin)
        } catch {
          // Window may be cross-origin or navigating
        }
      }, 500)
    }

    const closePopupBlockedModal = () => {
      isPopupBlockedModalVisible.value = false
      blockedMerchant.value = null
      unlockBodyScroll()
    }

    return {
      scrollContainer,
      redirectingId,
      isPopupBlockedModalVisible,
      blockedMerchant,
      selectedMerchantForDetail,
      isViewAllModalVisible,
      searchQuery,
      filteredMerchants,
      isBookingModuleEnabled,
      getMerchantImage,
      isUsingPhotoCover,
      handleImageError,
      openPropertyDetail,
      closePropertyDetail,
      openViewAllModal,
      closeViewAllModal,
      handleBookNow,
      closePopupBlockedModal
    }
  }
}
</script>

<style scoped>
/* Base Section (Mobile First) */
.booking-redirect-section {
  width: 100%;
  box-sizing: border-box;
  margin: 0;
}

/* Section Header */
.booking-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.01em;
}

.section-view-all {
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

.section-view-all:hover {
  color: var(--primary-color, #f93e3e);
}

/* Cards Track Layout */
.cards-track {
  display: flex;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
}

/* Mobile: Horizontal scroll snap */
.cards-track.is-multi {
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 2px;
}

.cards-track.is-multi::-webkit-scrollbar {
  display: none;
}

.cards-track.is-single .hotel-card {
  width: 100%;
  flex: 1 1 100%;
}

/* Individual Hotel Card (Minimalist Apple Standard, Thin Border, Pure CSS) */
.hotel-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e1e4ea;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  transition: border-color 0.2s ease;
  scroll-snap-align: start;
  flex: 0 0 240px;
  cursor: pointer;
  box-shadow: none;
}

.hotel-card:hover {
  border-color: #cbd5e1;
}

/* Card Media / Logo Banner */
.hotel-card-media {
  position: relative;
  height: 80px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #f1f5f9;
  padding: 10px;
  box-sizing: border-box;
  overflow: hidden;
  border-top-left-radius: 9px;
  border-top-right-radius: 9px;
}

.hotel-card-media.is-cover-photo {
  padding: 0;
  background: #1e293b;
}

.hotel-card-media.is-cover-photo .merchant-logo-img {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  display: block;
}

.logo-display-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.merchant-logo-img {
  max-height: 44px;
  max-width: 85%;
  object-fit: contain;
  transition: transform 0.25s ease;
}

.hotel-card:hover .hotel-card-media.is-cover-photo .merchant-logo-img {
  transform: scale(1.04);
}

.fallback-hotel-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  gap: 2px;
}

.fallback-code {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #64748b;
}

/* Card Body */
.hotel-card-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  box-sizing: border-box;
}

.property-info-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.property-name {
  font-size: 13px;
  font-weight: 600;
  color: #1b1e28;
  margin: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.branch-name {
  font-size: 11px;
  font-weight: 400;
  color: #717784;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Meta Rows */
.property-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-icon {
  flex-shrink: 0;
  color: #94a3b8;
}

.meta-text {
  font-size: 11px;
  color: #717784;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.phone-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #4b5563;
  font-size: 11px;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s ease;
}

.phone-chip:hover {
  color: var(--primary-color, #f93e3e);
}

.phone-chip-icon {
  color: #94a3b8;
  flex-shrink: 0;
}

/* CTA Button */
.hotel-card-action {
  margin-top: auto;
  padding-top: 4px;
}

.btn-book-now {
  width: 100%;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--primary-color, #f93e3e);
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s ease;
  box-sizing: border-box;
}

.btn-book-now:hover:not(:disabled) {
  opacity: 0.92;
}

.btn-book-now:disabled {
  opacity: 0.7;
  cursor: wait;
}

.btn-content-ready {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.arrow-icon {
  transition: transform 0.2s ease;
}

.btn-book-now:hover .arrow-icon {
  transform: translateX(2px);
}

.btn-content-loading {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.spinner-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Desktop Responsive Layout */
@media (min-width: 992px) {
  .booking-redirect-section {
    background-color: #ffffff;
    border-radius: 16px;
    padding: 24px;
    border: 1px solid #e1e4ea;
    box-shadow: none;
    margin-top: 24px;
    margin-bottom: 0;
  }

  .booking-section-header {
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 18px;
    font-weight: 520;
    color: #1b1e28;
  }

  /* Desktop: Horizontal scroll matching Limited offers & mobile */
  .cards-track,
  .cards-track.is-multi {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    padding-bottom: 10px;
    scrollbar-width: thin;
    scrollbar-color: rgba(209, 213, 219, 0.8) transparent;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
  }

  .cards-track::-webkit-scrollbar,
  .cards-track.is-multi::-webkit-scrollbar {
    display: block;
    height: 3px;
  }

  .cards-track::-webkit-scrollbar-track,
  .cards-track.is-multi::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 99px;
  }

  .cards-track::-webkit-scrollbar-thumb,
  .cards-track.is-multi::-webkit-scrollbar-thumb {
    background: rgba(209, 213, 219, 0.8);
    border-radius: 99px;
    transition: background 0.2s ease;
  }

  .cards-track::-webkit-scrollbar-thumb:hover,
  .cards-track.is-multi::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.9);
  }

  .cards-track .hotel-card {
    flex: 0 0 280px;
    width: 280px;
    min-width: 280px;
    max-width: 280px;
    scroll-snap-align: start;
  }

  .cards-track.is-single .hotel-card {
    flex: 0 0 280px;
    width: 280px;
    min-width: 280px;
    max-width: 280px;
  }
}

/* Skeleton Loading Shimmer */
.skeleton-card {
  pointer-events: none;
}

.skeleton-media {
  height: 80px;
  background: #f1f5f9;
}

.skeleton-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-line {
  border-radius: 6px;
  background: #f1f5f9;
}

.title-line {
  height: 16px;
  width: 75%;
}

.loc-line {
  height: 12px;
  width: 50%;
}

.btn-line {
  height: 32px;
  width: 100%;
  margin-top: 4px;
}

.shimmer {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Custom Modals (Pure CSS, No Bootstrap Overrides) */
.custom-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.view-all-backdrop {
  z-index: 10000;
}

.property-detail-backdrop {
  z-index: 10050;
}

.blocker-backdrop {
  z-index: 10100;
}

.custom-modal-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e1e4ea;
  width: 100%;
  display: flex;
  flex-direction: column;
  animation: popIn 0.2s ease;
  box-sizing: border-box;
  overflow: hidden;
}

.property-detail-card {
  max-width: 440px;
}

.view-all-card {
  max-width: 960px;
  max-height: 85vh;
}

.blocker-card {
  max-width: 360px;
  padding: 24px;
  text-align: center;
  align-items: center;
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.custom-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e1e4ea;
  box-sizing: border-box;
}

.custom-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #1b1e28;
  margin: 0;
}

.custom-modal-close-btn {
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.custom-modal-close-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}

/* Property Detail Modal Content */
.property-detail-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
}

.detail-logo-banner {
  height: 110px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
}

.detail-logo-banner.is-cover-photo {
  padding: 0;
  background: #1e293b;
}

.detail-logo-banner.is-cover-photo .detail-logo-img {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  display: block;
}

.detail-logo-img {
  max-height: 60px;
  max-width: 80%;
  object-fit: contain;
}

.detail-info-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
}

.detail-branch {
  font-size: 13px;
  color: #64748b;
  margin: -6px 0 4px 0;
}

.detail-meta-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.detail-icon-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-meta-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.detail-value {
  font-size: 13px;
  color: #334155;
  line-height: 1.4;
}

.detail-phone-link {
  font-size: 13px;
  color: var(--primary-color, #f93e3e);
  text-decoration: none;
  font-weight: 500;
}

.custom-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e1e4ea;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
}

.custom-modal-footer.flex-col {
  flex-direction: column;
  gap: 8px;
  width: 100%;
  border-top: none;
  padding: 0;
  margin-top: 16px;
}

.btn-detail-book {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: var(--primary-color, #f93e3e);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s ease;
  box-sizing: border-box;
}

.btn-detail-book:hover:not(:disabled) {
  opacity: 0.92;
}

.btn-cancel-action {
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #e1e4ea;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.btn-cancel-action:hover {
  background: #f8fafc;
  color: #1e293b;
}

/* View All Modal Elements */
.view-all-search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 20px 0 20px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e1e4ea;
  border-radius: 8px;
}

.search-icon {
  color: #94a3b8;
  flex-shrink: 0;
}

.search-input {
  border: none;
  background: transparent;
  outline: none;
  width: 100%;
  font-size: 13px;
  color: #0f172a;
}

.view-all-list-body {
  padding: 16px 20px 24px 20px;
  overflow-y: auto;
  max-height: 65vh;
  box-sizing: border-box;
}

.view-all-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.view-all-grid .hotel-card {
  flex: none;
  width: 100%;
}

.view-all-empty {
  text-align: center;
  padding: 32px;
  color: #94a3b8;
  font-size: 13px;
}

/* Blocker Elements */
.blocker-icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.blocker-desc {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
